import React, {useEffect, useState} from "react";
import {Button, Input, Popover} from "antd";
import UserDataTable from "./user-list/user-data-table";
import "react-toastify/dist/ReactToastify.css";
import {PrinterOutlined} from "@ant-design/icons/lib";
import {useParams} from "react-router-dom";
import {getName} from "../../utility/utility";
import {IItem} from './user-interfaces'
import {jsPDF} from 'jspdf'
import {autoTable} from 'jspdf-autotable'
import resources from "../../resources";
import server from "../../server";
import {searchCriteria} from './user-list/utils';
import SearchCriteria from "../../page-components/common/search-criteria/search-criteria";
import {SearchParams} from "../../page-components/common/search-criteria/type/search-criteria.type";
import "../../styles/component/user/userList.scss"
import {Segmented} from "antd/lib";
import LoadingSkeleton from "../../page-components/common/loading-skeleton/loading-skeleton";
import {UserPlus} from "lucide-react";

const User = () => {
    const [userData, setUserData] = useState<IItem[]>([]);
    const [isActiveUsers, setIsActiveUsers] = useState(true);
    const params: any = useParams();
    const tempResource = resources.commonButtonText;
    const [headingChange, setHeadingChange] = useState(false);
    const [sortField, setSortField] = useState("CREATED_ON");
    const [sortDirection, setSortDirection] =
        useState<"ASC" | "DESC" | undefined>("DESC");
    const [searchParams, setSearchParams] = useState<SearchParams | null>(null);
    const [pageNumber, setPageNumber] = useState(0);
    const pageSize = 10;
    const [loading, setLoading] = useState(true);
    const [openCreateUserModal, setOpenCreateUserModal] = useState(false);

    useEffect(() => {
        usersData(pageNumber, sortField, sortDirection);
    }, [isActiveUsers, pageNumber, params.id]);

    const handleNextPage = () => {
        setPageNumber(pageNumber + 1);
    }

    const handlePrevPage = () => {
        if (pageNumber > 0) {
            setPageNumber(pageNumber - 1);
        }
    };

    const handleSortChange = (field: string, direction: "ASC" | "DESC" | undefined) => {
        setSortField(field);
        setSortDirection(direction);
        usersData(pageNumber, field, direction);
    };

    const usersData = async (pageNumber: number, field?: any, direction?: any) => {
        if (isActiveUsers) {
            const payload: any = {
                "active": true,
                sortBy: field,
                sortDirection: direction,
                ...searchParams
            }
            if (searchParams && searchParams.roles) {
                payload.roles = [
                    searchParams.roles
                ]
            }
            setLoading(true)
            try {
                const response = await server.user.search(payload, pageNumber, pageSize);
                const newResponse = response.map((user: any) => {
                    const tempEmail = user?.email.toLowerCase();
                    const tempName = getName(user.name.firstName ?? '', user.name.middleName ?? '', user.name.lastName ?? '');
                    return {
                        ...user,
                        email: tempEmail,
                        status: user.active ? "Active" : "InActive",
                        name: tempName,
                        title: user.name.title,
                        firstName: user.name.firstName ?? '',
                        middleName: user.name.middleName ?? '',
                        lastName: user.name.lastName ?? '',
                    };
                })
                setUserData(newResponse);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false)
            }
        } else {
            const payload = {
                "active": false,
                sortBy: field,
                sortDirection: direction,
                ...searchParams
            }
            setLoading(true);
            try {
                const InActiveUsersResponse = await server.user.search(payload, pageNumber, pageSize);
                const newResponse = InActiveUsersResponse.map((user: any) => {
                    const tempEmail = user?.email.toLowerCase();
                    const tempName = getName(user.name.firstName ?? '', user.name.middleName ?? '', user.name.lastName ?? '');
                    return {
                        ...user, email: tempEmail,
                        status: user.active ? "Active" : "InActive",
                        name: tempName,
                        title: user.name.title,
                        firstName: user.name.firstName ?? '',
                        middleName: user.name.middleName ?? '',
                        lastName: user.name.lastName ?? '',
                    };
                })
                setUserData(newResponse);
            } catch (e) {
                console.log("Error while fetching data", e);
            } finally {
                setLoading(false)
            }
        }
    };

    const generatePDF = () => {
        const doc = new jsPDF("landscape", "pt", "a3");
        doc.setFontSize(16);
        doc.text("JSON Data PDF", doc.internal.pageSize.getWidth() / 2, 30, {
            align: "center",
        });
        let tableColumnHeaders = Object.keys(userData[2]).filter((key: string) => key !== "auditInfo");
        const tableRows = userData.map((row: any) => {
                const rowArr = tableColumnHeaders.map((key: string) => {
                    if (row[key]) {
                        return row[key].toString().trim();
                    }
                    return ""
                })
                return rowArr;
            }
        );
        autoTable(doc, {
            startY: 50,
            head: [tableColumnHeaders],
            body: tableRows,
            margin: {top: 50, left: 20, right: 20},
            styles: {
                fontSize: 10,
                cellPadding: 5,
                valign: "middle",
            },
            headStyles: {fillColor: [41, 128, 185], textColor: 255, fontSize: 12},
            alternateRowStyles: {fillColor: [245, 245, 245]},
            columnStyles: {auto: {cellWidth: "auto"}},
            theme: "striped",
        });
        doc.save(`users.pdf`);
    }

    const content = (
        <div className={"popup-container"} style={{width: 250, alignItems: "center"}}>
            <p>Proceed to download pdf file in "Downloads" directory?</p>
            <Button type={"primary"} onClick={generatePDF} style={{width: "100%", margin: "10px 0px"}}>
                {tempResource.downloadButton}
            </Button>
        </div>
    );

    const handleReset = () => {
        setHeadingChange(false);
    }

    const handleSearch = async (params: SearchParams) => {
        setHeadingChange(true);
        setSearchParams({...params});
        setLoading(true);
        try {
            const payload: any = {
                active: isActiveUsers,
                sortBy: sortField,
                sortDirection: sortDirection,
                ...params,
            };
            if (params.duration && Array.isArray(params.duration)) {
                const [startDate, endDate] = params.duration;
                payload.duration = {
                    startDate: startDate ? startDate : null,
                    endDate: endDate ? endDate : null,
                };
            }
            if (params.roles) {
                payload.roles = [
                    params.roles
                ]
            }

            const resp = await server.user.search(payload, 0, pageSize);

            const newResponse = resp.map((user: any) => {
                return {
                    ...user,
                    email: user?.email?.toLowerCase() || '',
                    status: user.active ? "Active" : "InActive",
                    name: getName(
                        user?.name?.firstName ?? '',
                        user?.name?.middleName ?? '',
                        user?.name?.lastName ?? ''
                    ),
                    title: user.name.title,
                    firstName: user.name.firstName ?? '',
                    middleName: user.name.middleName ?? '',
                    lastName: user.name.lastName ?? '',
                };
            });
            setPageNumber(0);
            setUserData(newResponse);
        } catch (error) {
            console.error('Search failed:', error);
        } finally {
            setLoading(false);
        }
    };

    function handleActiveUserView(value: string) {
        if (value === 'active') {
            setIsActiveUsers(true)
        } else {
            setIsActiveUsers(false)
        }
    }

    return (
        <div className={"user-container"}>
            <SearchCriteria
                searchCriteria={searchCriteria}
                onSearch={handleSearch}
                showReset={true}
                onReset={handleReset}
                title={"Search User"}
                renderTopContent={({values, onChange}) => (
                    <>
                        <div>
                            <label className={"label"}> Name</label>
                            <Input
                                placeholder="Enter name"
                                value={values.name || ''}
                                onChange={(e) => onChange('name', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className={"label"}> Email</label>
                            <Input
                                placeholder="Enter official email"
                                value={values.email || ''}
                                onChange={(e) => onChange('email', e.target.value)}
                            />
                        </div>
                        <div>
                            <div className="status-toggle-section">
                                <span className="status-toggle-label">User Status</span>
                                <Segmented
                                    className="status-toggle-buttons"
                                    options={[
                                        {
                                            label: 'Active',
                                            value: 'active'
                                        },
                                        {
                                            label: 'InActive',
                                            value: 'inactive'
                                        }
                                    ]}
                                    value={isActiveUsers ? 'active' : 'inactive'}
                                    onChange={(value) => handleActiveUserView(value)}
                                />
                            </div>
                        </div>
                    </>
                )}
            />

            <div>
                <div className={"user-heading"}>
                    <h4 className={"users-table-heading"}>{headingChange ? 'Showing search results' :
                        isActiveUsers ? "List of Active Users" : "List of Inactive Users"} </h4>

                    <div className={'user-actions'}>
                        <Button type={'primary'} onClick={() => setOpenCreateUserModal(true)}>
                            <UserPlus/> Create user
                        </Button>
                        {params.id === 'user' && <div className={"buttons-wrapper"}
                        >
                            <Popover
                                content={content}
                                title={"Download Users File"}
                                classNames={{
                                    root: "hrms-app print-confirm-popover",
                                }}
                            >
                                <Button type={"primary"} className={'print-btn'}>
                                    <PrinterOutlined style={{padding: "1px", fontSize: "16px"}}/>PRINT
                                </Button>
                            </Popover>
                        </div>}
                    </div>
                </div>

                <div>
                    <LoadingSkeleton loading={loading}>
                        <UserDataTable
                            sortField={sortField}
                            sortDirection={sortDirection}
                            isActiveUsers={isActiveUsers}
                            userData={userData}
                            userListType={params.id}
                            setUserData={setUserData}
                            handleSortChange={handleSortChange}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            handleNextPage={handleNextPage}
                            handlePrevPage={handlePrevPage}
                            openCreateUserModal={openCreateUserModal}
                            setOpenCreateUserModal={setOpenCreateUserModal}
                        />
                    </LoadingSkeleton>
                </div>
            </div>
        </div>
    );
};

export default User;